import { z } from 'zod';
import {
  PreparationErrorDetailsSchema,
  VendorResultSchema
} from '../external-event/external-event.visitor-management.types';

export const visitorVendorState$ = z.object({
  id: z.number(),
  booking_id: z.number(),
  vendor_id: z.string(),
  snapshot: z.unknown().optional().prefault(null), // null = vendor doesn't know about this booking
  last_contacted_at: z.string().nullable(),
  last_successful_at: z.string().nullable(),
  last_successful_request: z.record(z.string(), z.unknown()).nullable(),
  last_successful_response: z.record(z.string(), z.unknown()).nullable(),
  last_failed_at: z.string().nullable(),
  last_failed_request: z.record(z.string(), z.unknown()).nullable(),
  last_failed_response: z.record(z.string(), z.unknown()).nullable(),
  last_error_message: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  // Drift detection fields (only present when include_current_snapshots=true)
  current_snapshot: z.unknown().nullable().optional(),
  snapshot_error: z
    .discriminatedUnion('status', [
      z.object({
        status: z.literal('preparation_error'),
        message: z.string().nullable(),
        preparation_error_details: PreparationErrorDetailsSchema.optional()
      }),
      z.object({
        status: z.literal('not_eligible'),
        reason: z.string(), // Backend includes reason for snapshot_error
        message: z.string().nullable()
      })
    ])
    .nullable()
    .optional(),
  in_sync: z.boolean().optional()
});

// Booking details schemas
const bookingContact$ = z.object({
  id: z.number(),
  name: z.string().nullable().optional(),
  first_name: z.string().nullable().optional(),
  last_name: z.string().nullable().optional(),
  full_name: z.string().nullable().optional(),
  phone_numbers: z.array(z.unknown()).optional(),
  organisation: z.record(z.string(), z.unknown()).nullable().optional()
});

const bookingEvent$ = z.object({
  id: z.number(),
  start_date: z.string(),
  end_date: z.string()
});

const bookingStatus$ = z.object({
  id: z.number(),
  name: z.string()
});

const bookingType$ = z.object({
  id: z.number(),
  name: z.string()
});

const booking$ = z.object({
  id: z.number(),
  contact_id: z.number().optional(),
  contact: bookingContact$.optional(),
  events: z.array(bookingEvent$).optional(),
  status: bookingStatus$.optional(),
  booking_type: bookingType$.optional(),
  archived: z.boolean().optional(),
  note: z.string().nullable().optional()
});

// Booking with vendor states nested
export const bookingWithVendorStates$ = z.object({
  booking_id: z.number(),
  booking: booking$,
  vendors: z.record(z.string(), visitorVendorState$)
});

export const bookingWithVendorStatesListResponse$ = z.object({
  results: z.array(bookingWithVendorStates$),
  paging: z.object({
    total: z.number(),
    page: z.number(),
    count_per_page: z.number()
  })
});

// Legacy response for backwards compatibility
export const visitorVendorStateListResponse$ = z.object({
  results: z.array(visitorVendorState$),
  paging: z.object({
    total: z.number(),
    page: z.number(),
    count_per_page: z.number()
  })
});

// Manual sync response
const manualSyncVendorResult$ = VendorResultSchema;

export const manualSyncResponse$ = z.object({
  success: z.boolean(),
  booking_id: z.number(),
  vendors: z.array(manualSyncVendorResult$),
  summary: z.object({
    total: z.number(),
    succeeded: z.number(),
    preparation_errors: z.number(),
    api_errors: z.number(),
    not_eligible: z.number()
  })
});
